// Generates a downloadable PDF of a project's score (page images +
// fingering overlays) via `jspdf`. The mobile app does the same thing
// with `expo-print` against pre-computed static HTML; on web we draw
// directly into the PDF with vector primitives so digits stay
// searchable / sharp / copyable and we don't depend on the browser's
// print engine (which is what produced the misaligned digits in the
// previous print-button flow).
//
// Layout pipeline (mirrors `MobileApp/utils/pdfExport.ts`):
//   1. Parse each PNG's natural width/height from its base64 header.
//   2. Lay out every system at a known PDF-points coordinate system
//      (A4 portrait, fixed margins, fixed pill/digit sizing).
//   3. For each fingering group, compute pill rect + per-digit
//      positions in points, then draw image, pill, digits in that
//      order with `addImage` / `roundedRect` / `text`.
//   4. Trigger a download via `doc.save(...)`.
//
// Accidental rendering: jspdf has no native PDF blend-mode support
// (its GState constructor only accepts `opacity` / `stroke-opacity`),
// so we can't emit a `/BM /Lighten` ExtGState directly. To still get
// a `lighten`-blended notehead tint in the PDF — matching the on-
// screen `mix-blend-mode: lighten` exactly — we pre-composite each
// page image on an offscreen canvas (`globalCompositeOperation =
// 'lighten'`) BEFORE handing it to `doc.addImage`. The result: the
// alter colour is baked into the page bitmap, the surrounding paper
// stays untouched, and the fingering pills / pitch letters still
// render as crisp vector primitives on top.

import { jsPDF, GState } from 'jspdf';
import { buildLicenseNotice } from './licenseNotice';

import { accidentalBboxColor } from './accidentalBboxColor';

import type { ScoreSystemData } from '../hooks/usePdfStateQueue';
import type { FingeringSize } from '../components/FingeringOverlay';

export interface ExportProjectPdfOptions {
  /** Base64-encoded PNG strings, one per system. */
  pages: string[];
  /** Fingering groups per system (same index order as `pages`). */
  fingeringData: ScoreSystemData[];
  /** When false, alt fingerings (and their lighter pill) are skipped. */
  showAltFingerings: boolean;
  /** When false, main fingerings are skipped. Alts follow this off. */
  showFingerings?: boolean;
  /**
   * When true, tinted bboxes are drawn around every notehead whose
   * `alter !== 0`. Mirrors the on-screen accidental overlay.
   */
  showAccidental: boolean;
  /**
   * When true, the diatonic pitch letter (C..B from `pitchStep`) is
   * drawn centered on every notehead bbox in the system. Mirrors the
   * on-screen pitch overlay.
   */
  showPitch: boolean;
  /**
   * Live fingering size tier selected via the toolbar slider. The PDF
   * picks the corresponding entry from `PDF_FINGERING_SIZES` so the
   * downloaded document matches what the user just saw on screen.
   */
  fingeringSize: FingeringSize;
  /** Used for the downloaded filename — sanitized before use. */
  projectName: string;
  /** Draws a brand line in the bottom margin of every page. */
  attribution?: boolean;
  /** Named in the license notice in the PDF metadata. */
  projectId?: string;
}

// A4 portrait page geometry (PDF points = 1/72 inch).
const PAGE_W = 595;
const PAGE_H = 842;
const MARGIN = 24;
const CONTENT_W = PAGE_W - MARGIN * 2;
const CONTENT_BOTTOM = PAGE_H - MARGIN;
const SYSTEM_GAP = 12; // vertical breathing room between systems

// Sits in the empty bottom margin (`PAGE_H - 10` is below
// `CONTENT_BOTTOM`) so it cannot collide with a staff.
const ATTRIBUTION_LINE =
  'Fingerings generated with PineappleMusicLab \u2014 pineapplemusiclab.com';

// Fingering pill / digit sizing for the PDF, keyed by the
// `FingeringSize` tier the user picked on screen.
//
// Sizing rationale:
//   - `medium` (the default) keeps the exact numbers we shipped
//     before the slider — 9 pt matches the conventional fingering
//     number size in printed/engraved music, and the geometry is
//     tuned so a single-digit pill renders nearly square
//     (groupWidth ≈ numHeight + 2·padY) instead of as a tall
//     capsule. Multi-digit pills end up with full semicircular
//     caps because `borderRadius = groupWidth / 2`, and jspdf
//     auto-clamps that to min(w, h)/2 internally.
//   - `small` scales down ~30% so dense scores stay readable; the
//     `gapBetween` stays at 4 pt so the alt pill doesn't collide
//     with the main pill at this tier.
//   - `large` scales up ~50% — picked so the PDF stays close in
//     feel to the live large view (which is ~30% larger than the
//     medium tier) while accounting for the PDF being rendered at
//     a narrower CSS width than a desktop browser.
interface PdfSizeConfig {
  fontSize: number;
  groupWidth: number;
  numHeight: number;
  padY: number;
  gapBetween: number;
  borderRadius: number;
}

const PDF_FINGERING_SIZES: Record<FingeringSize, PdfSizeConfig> = {
  small: {
    fontSize: 6,
    groupWidth: 7,
    numHeight: 6,
    padY: 1,
    gapBetween: 3,
    borderRadius: 7 / 2,
  },
  medium: {
    fontSize: 9,
    groupWidth: 10,
    numHeight: 9,
    padY: 1,
    gapBetween: 4,
    borderRadius: 10 / 2,
  },
  large: {
    fontSize: 13,
    groupWidth: 15,
    numHeight: 13,
    padY: 1,
    gapBetween: 5,
    borderRadius: 15 / 2,
  },
};

// Fill colours for the group pill, mirroring the live SVG view's
// rgb(238, 241, 254) at 0.8 (main) / 0.35 (alt).
const PILL_RGB: [number, number, number] = [238, 241, 254];
const MAIN_PILL_OPACITY = 0.8;
const ALT_PILL_OPACITY = 0.35;

// Digit colours, also matching the live view.
const MAIN_DIGIT_COLOR = '#5c6bff';
const ALT_DIGIT_COLOR = '#bfc6ff';

// Diatonic pitch letters, indexed by `pitchStep` (0..6). Matches the
// `PITCH_STEP_LETTERS` table in `AI_core/omr_pipeline/richnotes.py`.
const PITCH_LETTERS = ['C', 'D', 'E', 'F', 'G', 'A', 'B'] as const;
// White letter + thin black rim. The rim is what makes white legible
// against white paper / hollow noteheads (otherwise the letter would
// just disappear); against filled-black noteheads, the white fill
// itself gives max contrast. Same trick used on highway signage /
// map labels. We avoid hues here so the letter doesn't visually
// collide with any of the bbox colours (sharp orange, double-sharp
// pink, flat cyan, double-flat violet).
const PITCH_LETTER_COLOR = '#ffffff';
const PITCH_RIM_COLOR = '#000000';
// Rim width is proportional to the per-system font size so it stays
// visually consistent across small/large systems. 8% of font size
// matches the SVG overlay's `strokeWidth = pitchFontSize * 0.08`.
const PITCH_RIM_WIDTH_RATIO = 0.08;
// Floor for the pitch letter size — derived per-system from the
// median notehead bbox height. Upper bound is `cfg.fontSize` (the
// chosen tier's fingering digit size) so the pitch letter never
// outgrows the digits on the same page.
const PITCH_FONT_SIZE_MIN = 4; // ~5 CSS px — still legible in print

export async function exportProjectPdf(
  opts: ExportProjectPdfOptions
): Promise<void> {
  const {
    pages,
    fingeringData,
    showAltFingerings,
    showFingerings = true,
    showAccidental,
    showPitch,
    fingeringSize,
    projectName,
    attribution,
    projectId,
  } = opts;
  if (!pages || pages.length === 0) {
    throw new Error('No systems to export.');
  }

  // Looked up once for the whole document — the slider value is
  // captured at download time and applied uniformly across every
  // system. There is no per-system override path.
  const cfg = PDF_FINGERING_SIZES[fingeringSize];

  const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'portrait' });
  // Embed a single solid font; bold weight matches the live overlay.
  doc.setFont('helvetica', 'bold');

  // Brand attribution via metadata (visible in PDF readers' "Document
  // Properties", never in the Save-As dialog) so the filename stays
  // user-named. jsPDF's public types don't expose `producer`; the
  // default "jsPDF x.y.z" it writes is fine.
  doc.setProperties({
    title: projectName,
    subject: buildLicenseNotice(projectId),
    creator: 'PineappleMusicLab',
  });

  // Re-usable GStates so we don't allocate one per pill. PDF
  // graphics state stays "sticky" until the next setGState call, so
  // we always reset to `opaqueGState` before drawing digits.
  const mainPillGState = new GState({ opacity: MAIN_PILL_OPACITY });
  const altPillGState = new GState({ opacity: ALT_PILL_OPACITY });
  const opaqueGState = new GState({ opacity: 1 });

  let yCursor = MARGIN;
  let firstPage = true;

  for (let pageIdx = 0; pageIdx < pages.length; pageIdx++) {
    const base64 = pages[pageIdx];
    const dims = getPngDimensions(base64);
    // If we can't parse the header (unexpected PNG / corrupt data)
    // we fall back to a 1:1 layout — overlays at least stay
    // self-consistent even if alignment is approximate.
    const imgW = dims?.width ?? CONTENT_W;
    const imgH = dims?.height ?? Math.round(CONTENT_W * 0.6);

    const scale = CONTENT_W / imgW;
    const displayH = imgH * scale;

    const groups = fingeringData[pageIdx]?.fingeringGroupList ?? [];

    // First pass: how much vertical padding does this system need
    // so badges drawn above/below the image aren't clipped by the
    // next system?
    let maxOverflowTop = 0;
    let maxOverflowBottom = 0;
    for (const group of groups) {
      const { mains, alts } = splitFingerings(
        group,
        showAltFingerings,
        showFingerings
      );
      if (mains.length === 0 && alts.length === 0) continue;
      const cy = group.coordinate.y * scale;
      const mainH =
        mains.length > 0 ? mains.length * cfg.numHeight + cfg.padY * 2 : 0;
      const altH =
        alts.length > 0 ? alts.length * cfg.numHeight + cfg.padY * 2 : 0;
      const totalH =
        mainH + (mainH > 0 && altH > 0 ? cfg.gapBetween : 0) + altH;
      if (group.coordinate.upward) {
        const groupTopY = cy - totalH;
        if (groupTopY < 0) {
          maxOverflowTop = Math.max(maxOverflowTop, -groupTopY);
        }
      } else {
        const groupBottomY = cy + totalH;
        if (groupBottomY > displayH) {
          maxOverflowBottom = Math.max(
            maxOverflowBottom,
            groupBottomY - displayH
          );
        }
      }
    }

    const padTop = Math.ceil(maxOverflowTop) + 4;
    const padBottom = Math.ceil(maxOverflowBottom) + 4;
    const systemBlockH = padTop + displayH + padBottom;

    // Page-break: if this entire system block won't fit on the
    // current page, start a fresh one. (We never split a system
    // across a page break — the user-visible alignment math relies
    // on the image and overlays sharing one frame.)
    if (!firstPage && yCursor + systemBlockH > CONTENT_BOTTOM) {
      doc.addPage();
      yCursor = MARGIN;
    }
    firstPage = false;

    // The image's top-left in PDF points. Overlays use coordinates
    // relative to this same origin.
    const imageX = MARGIN;
    const imageY = yCursor + padTop;

    // Page-image source: either the raw PNG or — when the accidental
    // toggle is on AND this system has notehead data — a canvas-
    // composited PNG where each accidental bbox region has been
    // `lighten`-blended with its alter colour. jspdf has no native
    // blend-mode support (its GState only emits `/ca` and `/CA` for
    // alpha), so we bake the blend into the page bitmap before it
    // ever reaches the PDF. This mirrors the on-screen behaviour
    // exactly: black noteheads pick up the alter tint, white paper
    // stays untouched, no rectangular border anywhere.
    const noteheadDataList = fingeringData[pageIdx]?.noteheadDataList ?? [];
    let pageImageDataUrl = `data:image/png;base64,${base64}`;
    if (showAccidental && noteheadDataList.length > 0) {
      try {
        pageImageDataUrl = await compositeAccidentalMask(
          base64,
          noteheadDataList
        );
      } catch (e) {
        // If compositing throws (canvas context unavailable, image
        // failed to decode, tainted canvas — none of which should
        // happen with a same-origin data URL, but be defensive),
        // fall back to the raw page image. The PDF then lacks the
        // accidental tint for this page but everything else still
        // renders correctly.
        console.warn(
          `exportProjectPdf: accidental mask composite failed for page ${pageIdx}; falling back to raw image`,
          e
        );
      }
    }

    doc.addImage(pageImageDataUrl, 'PNG', imageX, imageY, CONTENT_W, displayH);

    // Second pass: draw the pills and digits in the order that
    // matches the live SVG view — pill behind, then digits on top.
    for (const group of groups) {
      const { mains, alts } = splitFingerings(
        group,
        showAltFingerings,
        showFingerings
      );
      if (mains.length === 0 && alts.length === 0) continue;

      const cx = imageX + group.coordinate.x * scale;
      const cy = imageY + group.coordinate.y * scale;
      const isUp = group.coordinate.upward;

      const mainH =
        mains.length > 0 ? mains.length * cfg.numHeight + cfg.padY * 2 : 0;
      const altH =
        alts.length > 0 ? alts.length * cfg.numHeight + cfg.padY * 2 : 0;

      // For each subgroup compute the pill's top-y and the list of
      // digit positions in draw order. Cursor walks away from `cy`
      // in the same direction the live view uses (upward groups
      // stack mains above the note, then alts above the mains;
      // downward groups stack mains below the note, then alts).
      let mainsTopY = 0;
      let altsTopY = 0;
      let mainDigitYs: number[] = [];
      let altDigitYs: number[] = [];

      if (isUp) {
        let cursor = cy;
        if (mains.length > 0) {
          cursor -= mainH;
          mainsTopY = cursor;
          mainDigitYs = mains
            .slice()
            .reverse()
            .map((_, i) => digitCenterY(cursor, i, cfg));
        }
        if (alts.length > 0) {
          cursor -= cfg.gapBetween + altH;
          altsTopY = cursor;
          altDigitYs = alts
            .slice()
            .reverse()
            .map((_, i) => digitCenterY(cursor, i, cfg));
        }
      } else {
        let cursor = cy;
        if (mains.length > 0) {
          mainsTopY = cursor;
          mainDigitYs = mains.map((_, i) => digitCenterY(cursor, i, cfg));
          cursor += mainH;
        }
        if (alts.length > 0) {
          cursor += cfg.gapBetween;
          altsTopY = cursor;
          altDigitYs = alts.map((_, i) => digitCenterY(cursor, i, cfg));
        }
      }

      const pillX = cx - cfg.groupWidth / 2;

      // Pills: jspdf's roundedRect takes (x, y, w, h, rx, ry,
      // style). We use 'F' (fill, no stroke) and an opacity GState
      // so the underlying staff line shows through.
      if (mains.length > 0) {
        doc.setFillColor(PILL_RGB[0], PILL_RGB[1], PILL_RGB[2]);
        doc.setGState(mainPillGState);
        doc.roundedRect(
          pillX,
          mainsTopY,
          cfg.groupWidth,
          mainH,
          cfg.borderRadius,
          cfg.borderRadius,
          'F'
        );
      }
      if (alts.length > 0) {
        doc.setFillColor(PILL_RGB[0], PILL_RGB[1], PILL_RGB[2]);
        doc.setGState(altPillGState);
        doc.roundedRect(
          pillX,
          altsTopY,
          cfg.groupWidth,
          altH,
          cfg.borderRadius,
          cfg.borderRadius,
          'F'
        );
      }

      // Reset graphics state before drawing digits so they render
      // at full opacity even if the previous pill was the alt one.
      doc.setGState(opaqueGState);
      doc.setFontSize(cfg.fontSize);

      if (mains.length > 0) {
        doc.setTextColor(MAIN_DIGIT_COLOR);
        const orderedMains = isUp ? mains.slice().reverse() : mains;
        for (let i = 0; i < orderedMains.length; i++) {
          doc.text(String(orderedMains[i]), cx, mainDigitYs[i], {
            align: 'center',
            baseline: 'middle',
          });
        }
      }
      if (alts.length > 0) {
        doc.setTextColor(ALT_DIGIT_COLOR);
        const orderedAlts = isUp ? alts.slice().reverse() : alts;
        for (let i = 0; i < orderedAlts.length; i++) {
          doc.text(String(orderedAlts[i]), cx, altDigitYs[i], {
            align: 'center',
            baseline: 'middle',
          });
        }
      }
    }

    // Pitch-letter pass. Accidentals were already baked into the
    // page image above via `compositeAccidentalMask`; only the pitch
    // letters remain as a separate vector pass so they stay crisp
    // and selectable.
    if (showPitch && noteheadDataList.length > 0) {
      // Letter size is derived once per system from the median
      // notehead bbox height (in PDF points). Doing it per-system
      // keeps the letters consistent within a row even if a few
      // noteheads happen to be drawn larger (e.g. cue-size variants).
      const heights = noteheadDataList
        .map((nd) =>
          nd.y2 != null && nd.y1 != null ? (nd.y2 - nd.y1) * scale : 0
        )
        .filter((hh) => hh > 0)
        .sort((a, b) => a - b);
      const medianHeight =
        heights.length > 0 ? heights[Math.floor(heights.length / 2)] : 0;
      const pitchFontSize = Math.max(
        PITCH_FONT_SIZE_MIN,
        Math.min(cfg.fontSize, medianHeight * 0.75)
      );

      doc.setFontSize(pitchFontSize);
      // Pre-compute every letter position once so the rim + fill
      // passes walk the same list — keeps the two halves perfectly
      // aligned even if `pitchStep` filtering changes later.
      const letterDraws: Array<{
        letter: string;
        cx: number;
        cy: number;
      }> = [];
      for (const nd of noteheadDataList) {
        if (
          nd.x1 == null ||
          nd.y1 == null ||
          nd.x2 == null ||
          nd.y2 == null ||
          nd.pitchStep == null
        )
          continue;
        const letter =
          nd.pitchStep >= 0 && nd.pitchStep < PITCH_LETTERS.length
            ? PITCH_LETTERS[nd.pitchStep]
            : '?';
        letterDraws.push({
          letter,
          cx: imageX + ((nd.x1 + nd.x2) / 2) * scale,
          cy: imageY + ((nd.y1 + nd.y2) / 2) * scale,
        });
      }

      // Rim pass: stroke-only black outline drawn at *double* the
      // visible rim width. The fill pass on top will hide the inner
      // half of the stroke, leaving only the outer half — same
      // effect as SVG `paint-order: stroke` (which renders the
      // stroke behind the fill). jspdf has no native paint-order
      // control, so the two-pass trick is the cleanest way to keep
      // the letter at its full glyph weight while still showing a
      // crisp rim. Rounded joins prevent angular letter terminals
      // (e.g. on "F", "A") from spiking outward.
      const rimWidthVisible = pitchFontSize * PITCH_RIM_WIDTH_RATIO;
      doc.setDrawColor(PITCH_RIM_COLOR);
      doc.setLineWidth(rimWidthVisible * 2);
      doc.setLineJoin('round');
      doc.setGState(opaqueGState);
      for (const d of letterDraws) {
        doc.text(d.letter, d.cx, d.cy, {
          align: 'center',
          baseline: 'middle',
          renderingMode: 'stroke',
        });
      }

      // Fill pass: yellow letter on top, covering the inner half of
      // the rim. Default `renderingMode` is "fill" — no need to set
      // it explicitly.
      doc.setTextColor(PITCH_LETTER_COLOR);
      for (const d of letterDraws) {
        doc.text(d.letter, d.cx, d.cy, {
          align: 'center',
          baseline: 'middle',
        });
      }

      // Restore the default fingering font size so the next page's
      // digit pass picks up where this one left off.
      doc.setFontSize(cfg.fontSize);
    }

    yCursor += systemBlockH + SYSTEM_GAP;
  }

  // Drawn last so it never interacts with the layout cursor and
  // covers pages created by `doc.addPage()`.
  if (attribution) {
    const total = doc.getNumberOfPages();
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(0);
    doc.setGState(opaqueGState);
    for (let p = 1; p <= total; p++) {
      doc.setPage(p);
      doc.text(ATTRIBUTION_LINE, PAGE_W / 2, PAGE_H - 10, { align: 'center' });
    }
  }

  // `_fingering` suffix matches both MusicXML download paths.
  doc.save(`${sanitizeName(projectName)}_fingering.pdf`);
}

// Splits a fingering group into its main + alt digit arrays, honouring
// the show-alts toggle so the PDF matches what's on screen.
function splitFingerings(
  group: ScoreSystemData['fingeringGroupList'][number],
  showAlt: boolean,
  showMain = true
): { mains: number[]; alts: number[] } {
  const mains: number[] = [];
  const alts: number[] = [];
  for (const fn of group.fingeringNumList ?? []) {
    if (showMain && fn.mainFingering > 0) mains.push(fn.mainFingering);
    if (showMain && showAlt && fn.altFingering > 0) alts.push(fn.altFingering);
  }
  return { mains, alts };
}

// Y center of the i-th digit cell inside a pill whose top edge is at
// `pillTopY`. Each cell is `cfg.numHeight` tall with `cfg.padY` of
// padding above the top cell. `cfg` comes from the chosen size tier
// so digits stay centered within their pill at every slider stop.
function digitCenterY(pillTopY: number, i: number, cfg: PdfSizeConfig): number {
  return pillTopY + cfg.padY + cfg.numHeight * (i + 0.5);
}

// Parse a PNG's width/height from its base64-encoded header. The
// IHDR chunk's width (uint32 BE) is at byte 16, height at byte 20 —
// we only need to decode the first 24 bytes.
function getPngDimensions(
  base64: string
): { width: number; height: number } | null {
  try {
    const head = base64.substring(0, 32);
    const binary = atob(head);
    if (binary.length < 24) return null;
    const b = (i: number) => binary.charCodeAt(i) & 0xff;
    // PNG signature check — fail loudly on JPEGs / corrupt data so
    // we surface "fallback was used" via the dimensions sentinel.
    if (b(0) !== 0x89 || b(1) !== 0x50 || b(2) !== 0x4e || b(3) !== 0x47) {
      return null;
    }
    const width = (b(16) << 24) | (b(17) << 16) | (b(18) << 8) | b(19);
    const height = (b(20) << 24) | (b(21) << 16) | (b(22) << 8) | b(23);
    if (width <= 0 || height <= 0) return null;
    return { width, height };
  } catch {
    return null;
  }
}

// Renders the page image into an offscreen canvas and lighten-blends
// a coloured rect over each non-natural notehead bbox, then returns
// the composited image as a PNG data URL. Mirrors the live SVG
// overlay's `mix-blend-mode: lighten` exactly — per-channel
// `max(backdrop, source)`, so black noteheads pick up the alter
// colour while white paper stays untouched. The output is fed
// straight into `doc.addImage`, replacing the raw page PNG.
//
// Coords here are in NATIVE IMAGE PIXEL SPACE (same as the
// noteheadDataList payload). The PDF's `addImage` is what scales the
// final bitmap down to the PDF's content width, so we don't have to
// worry about points-vs-pixels here.
async function compositeAccidentalMask(
  base64Png: string,
  noteheads: ScoreSystemData['noteheadDataList']
): Promise<string> {
  if (!noteheads || noteheads.length === 0) {
    return `data:image/png;base64,${base64Png}`;
  }
  const img = await loadImage(`data:image/png;base64,${base64Png}`);
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    // No 2D context (extremely rare; usually means the canvas was
    // GPU-blocked) — fall back to the raw image.
    return `data:image/png;base64,${base64Png}`;
  }
  // Flatten the page onto white BEFORE the lighten pass. The live
  // view gets this for free from the page's CSS background showing
  // through any transparent PNG regions, but a fresh canvas starts
  // fully transparent — so if the source PNG has any transparent
  // pixels inside an accidental bbox, `lighten` against transparent
  // (treated as `(0,0,0,0)`) would produce the alter colour as a
  // solid block. Painting white first replicates the on-screen
  // backdrop: transparent paper → white → `max(white, colour) =
  // white`, exactly what we want.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0);
  // `lighten` is the per-channel `max(backdrop, source)` blend —
  // identical to the on-screen `mix-blend-mode: lighten`. Sticky
  // until reset, but we don't draw anything else here so it's fine
  // to leave it set.
  ctx.globalCompositeOperation = 'lighten';
  for (const nd of noteheads) {
    if (nd.alter === 0) continue;
    if (nd.x1 == null || nd.y1 == null || nd.x2 == null || nd.y2 == null)
      continue;
    const w = nd.x2 - nd.x1;
    const h = nd.y2 - nd.y1;
    if (w <= 0 || h <= 0) continue;
    ctx.fillStyle = accidentalBboxColor(nd.alter);
    ctx.fillRect(nd.x1, nd.y1, w, h);
  }
  return canvas.toDataURL('image/png');
}

// Promise wrapper around `Image.onload` so the composite helper can
// `await` decoding. Data URLs decode synchronously in spec but the
// `.complete`/`.naturalWidth` properties are only reliable after
// onload fires, so we wait.
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () =>
      reject(new Error(`Failed to decode image for accidental mask composite`));
    img.src = src;
  });
}

function sanitizeName(name: string): string {
  const trimmed = (name || 'Score').trim();
  // Strip filesystem-hostile characters so browsers' "Save As"
  // suggestions don't choke on slashes/quotes etc.
  return trimmed.replace(/[\\/:*?"<>|]/g, '').slice(0, 80) || 'Score';
}
